import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBooking } from "@/lib/booking";
import { useI18n } from "@/lib/i18n";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Hero } from "@/components/stay/Hero";
import { Marquee } from "@/components/stay/Marquee";
import { Bento } from "@/components/stay/Bento";
import { TitleBar, Overview } from "@/components/stay/Overview";
import { Amenities } from "@/components/stay/Amenities";
import { DatesSection } from "@/components/stay/DatesSection";
import { BookingWidget } from "@/components/stay/BookingWidget";
import { RequestDialog } from "@/components/stay/RequestDialog";
import { Reviews } from "@/components/stay/Reviews";
import { Location, Rules } from "@/components/stay/LocationRules";
import { Story } from "@/components/stay/Story";
import { MobileBar } from "@/components/stay/MobileBar";
import { OtherUnits } from "@/components/stay/OtherUnits";

export default function StayPage() {
  const { slug: param } = useParams();
  const { pick } = useI18n();
  const [prop, setProp] = useState(null);
  const [count, setCount] = useState(1);
  const [units, setUnits] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [state, setState] = useState("loading");
  const [open, setOpen] = useState(false);
  const b = useBooking(prop, blocked);

  const loadAvail = useCallback(async (slug) => {
    try {
      const { data } = await api.get(`/properties/${slug}/availability`);
      setBlocked(Array.isArray(data?.blocked) ? data.blocked : []);
    } catch (e) { setBlocked([]); }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: list } = await api.get("/properties");
        const arr = Array.isArray(list) ? list : [];
        const p = param ? arr.find((x) => x.slug === param) : arr[0];
        if (!alive) return;
        setCount(arr.length);
        setUnits(arr);
        if (!p) { setState("missing"); return; }
        setProp(p);
        setState("ok");
        loadAvail(p.slug);
      } catch (e) { if (alive) setState("error"); }
    })();
    return () => { alive = false; };
  }, [param, loadAvail]);

  useEffect(() => {
    if (prop) document.title = `Samara Beach House · ${pick(prop.title)}`;
  }, [prop, pick]);

  if (state !== "ok") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink text-paper" data-testid="stay-page-status">
        <p className="font-display text-5xl italic">{state === "loading" ? "Samara Beach House" : "No disponible"}</p>
        {state !== "loading" && <Link to="/" className="font-mono text-xs uppercase tracking-[0.3em] text-gold">← Samara Beach House</Link>}
      </div>
    );
  }

  const photos = prop.photos || [];
  const storyPhoto = photos.find((p) => /mural|octopus/i.test(p.caption || "")) || photos[photos.length - 1];

  return (
    <div className="site bg-paper" data-testid="stay-page">
      <SmoothScroll />
      <Header overHero showStays={count > 1} />
      <Hero prop={prop} b={b} onRequest={() => setOpen(true)} site={units[0]?.slug === prop.slug} />
      <main className="mx-auto max-w-[1280px] px-5 pt-16 sm:px-8 sm:pt-24">
        <TitleBar prop={prop} />
        <div className="mt-8"><Bento prop={prop} /></div>
        <div className="grid grid-cols-1 gap-12 pb-24 pt-14 lg:grid-cols-12 lg:gap-16">
          <div className="space-y-14 lg:col-span-7">
            <Overview prop={prop} />
            <DatesSection b={b} synced={!!prop.last_synced_at} />
            <Amenities prop={prop} />
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28"><BookingWidget prop={prop} b={b} onRequest={() => setOpen(true)} /></div>
          </aside>
        </div>
      </main>
      <Marquee />
      <Story photo={storyPhoto} />
      <Reviews prop={prop} />
      <Location prop={prop} />
      <Rules prop={prop} />
      <OtherUnits units={units.filter((u) => u.slug !== prop.slug)} />
      <Footer prop={prop} />
      <MobileBar b={b} onRequest={() => setOpen(true)} />
      <RequestDialog open={open} onOpenChange={setOpen} prop={prop} b={b} onBooked={() => loadAvail(prop.slug)} />
    </div>
  );
}
